import { Form, FormInstance, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import SupportedFormats from "views/uikit/FormFields/DragAndDropBase/SupportedFormats";
import { folderFormats } from "views/uikit/FormFields/FoldersDropBox";
import { FoldersPathsForm } from "./FoldersPathsForm";
import AddDataModal from "./AddDataModal";
import styles from "./ScanImport.module.scss";

const FORM_ID = "folders-paths-form";

interface FolderPathsModalProps {
  isOpen: boolean;
  onClose: () => void;
  form: FormInstance;
  isValidating: boolean;
  onSubmit: (values: { paths: string[] }) => void;
}

/** Type one or more folder paths; "Add" submits them and closes. */
export default function FolderPathsModal({
  isOpen,
  onClose,
  form,
  isValidating,
  onSubmit,
}: FolderPathsModalProps) {
  // The submit button lives in the dialog footer, outside the form, so it reads the same
  // rules the form's own button used to.
  const paths: string[] = Form.useWatch("paths", form) || [];
  const entered = paths.filter((path) => path?.trim()).length;
  const addDisabledReason = paths.some((path) => !path)
    ? "Enter a folder path in every row"
    : new Set(paths).size !== paths.length
      ? "The same path is listed twice"
      : "";

  return (
    <AddDataModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select folder"
      subTitle="Enter folders that are already on the server, relative to the datasets directory."
      footerInfo={
        <>
          {entered
            ? `${entered} folder${entered === 1 ? "" : "s"} entered`
            : "No folder entered yet"}
          <Tooltip
            placement="top"
            title={<SupportedFormats formatList={folderFormats} />}
          >
            <InfoCircleOutlined
              className={styles.footerInfoIcon}
              aria-label="Supported formats"
            />
          </Tooltip>
        </>
      }
      addDisabledReason={addDisabledReason}
      submitFormId={FORM_ID}
    >
      <FoldersPathsForm
        form={form}
        onFinish={onSubmit}
        isValidating={isValidating}
        embedded
      />
    </AddDataModal>
  );
}
